import { Box } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  LIST_DROPDOWN_VALUES,
  ListDropdown,
} from 'ui-components/dist/ListDropdown';
import { Modal, ModalBody, ModalHeaderDefault } from 'ui-components/dist/Modal';
import { SavedList } from '~/clientApi/savedLists.model';
import { FEATURE_FLAGS } from '~/constants';
import { useGetSavedLists } from '~/hooks/api/useSavedLists';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { SavedListContextType } from '~/screens/SavedListsScreen/SavedListsScreen.types';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ManagerType } from '~/types';

import { SavedListsModalComponent } from './SavedListsModal.component';
import { SAVED_LIST_PAGESIZE } from './SavedListsModal.constants';
import { calculateModalHeight } from './SavedListsModal.utils';

export interface ISavedListsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateListOpen: () => void;
  onItemClick: (item: SavedList) => void;
}

export const SavedListsModal: React.FC<ISavedListsModalProps> = ({
  isOpen,
  onClose,
  onCreateListOpen,
  onItemClick,
}) => {
  const defaultDropdownContextValue = LIST_DROPDOWN_VALUES.AGENCY_LISTS;
  const [savedListsContext, setSavedListsContext] =
    useState<SavedListContextType>(defaultDropdownContextValue);
  const manager: ManagerType | undefined = useSelector(selectTalentManager);
  const isListsAgencyEnabled = useFeatureFlag(
    FEATURE_FLAGS.foamSavedListAgencies,
  );
  const hasAgenciesEnabled = Boolean(manager?.agency?.enabled);

  const handleOnCloseModal = useCallback(() => {
    onClose();

    setSavedListsContext(defaultDropdownContextValue);
  }, [defaultDropdownContextValue, onClose]);

  const isAgencyEnabled = isListsAgencyEnabled && hasAgenciesEnabled;
  const {
    data,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage,
    isLoading,
  } = useGetSavedLists({
    saved: true,
    pageSize: SAVED_LIST_PAGESIZE,
    enableAgencyLists:
      savedListsContext === LIST_DROPDOWN_VALUES.AGENCY_LISTS &&
      isAgencyEnabled,
  });

  const { minHeight } = useMemo(() => {
    return calculateModalHeight(data?.totalItems + 1 ?? 1);
  }, [data?.totalItems]);

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isLoading && !isFetchingNextPage) {
      return fetchNextPage();
    }
    return Promise.resolve();
  }, [hasNextPage, isLoading, isFetchingNextPage, fetchNextPage]);

  const handleChangeListsContextDropdowns = useCallback(
    (selectedValue: SavedListContextType) => {
      setSavedListsContext(selectedValue);
    },
    [],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleOnCloseModal}>
      <ModalHeaderDefault title="Add to:" />

      <ModalBody>
        {hasAgenciesEnabled && (
          <Box marginTop={4}>
            <ListDropdown
              selectedValue={savedListsContext}
              prefixTrackIds="foam"
              onChange={handleChangeListsContextDropdowns}
            />
          </Box>
        )}

        <Box marginTop={4}>
          <SavedListsModalComponent
            isLoading={isLoading}
            items={data?.results ?? []}
            onFetchNextPage={handleFetchNextPage}
            onItemClick={onItemClick}
            totalItems={data?.totalItems ?? 0}
            onCreateNewList={onCreateListOpen}
            minHeight={minHeight}
          />
        </Box>
      </ModalBody>
    </Modal>
  );
};
